import { Observable } from '@legendapp/state'
import { ColumnDef, RowData, SortingState, Table } from '@tanstack/react-table'
import { createContext, useContext } from 'react'

import { Option } from '../MultiSelectDropdown'
import { TanstackTable } from './Table'

export interface FilterDropdownProps {
  id?: string
  label: string // The label displayed for the filter
  type: 'text' | 'date' | 'dropdown' | 'checkbox' // The type of input for the filter
  value: any // The current value of the filter
  result?: any
  options?: Option[] // Optional array of options for dropdown type filters
  isSplitRow?: boolean
  isMultiSelect?: boolean
}

export type TableState$<T extends RowData> = Observable<{
  id?: string
  table?: Table<T>
  isSortable: boolean
  filters?: FilterDropdownProps[]
  data: T[]
  columns: ColumnDef<T, any>[]
  hideColumns?: string[]
  onApply?: (value: any) => void
  totalPage: number
  totalItem: number
  pageSize?: number | 20
  sort?: SortingState
  setSort: (value: any) => void
  setPageSize: (value: any) => void
  setPagination: (value: any) => void
  pagination?: number | 0
  enableRowSelection?: boolean
  selectedRows?: T[]
  onRowSelectionChange?: (selectedRows: T[]) => void
}>

// Define the context interface
export type TableLayoutContext<T extends RowData> = {
  state$: TableState$<T>
}

// Create the context
const TableContext = createContext<TableLayoutContext<any> | null>(null)

// Create the context provider
export const TableContextProvider = <T extends RowData>({ state$ }: TableLayoutContext<T>) => {
  return (
    <TableContext.Provider value={{ state$: state$ as any }}>
      <TanstackTable />
    </TableContext.Provider>
  )
}

// Custom hook for using the context
export const useTableContext = () => {
  const context = useContext(TableContext)

  if (context === null) {
    throw new Error('useLayoutContext must be used within a TableContextProvider')
  }

  return context
}
